import { useEffect, useRef, useState } from "react";
import type { SearchIndexEntry } from "@/types";
import { formatSearchResultDate } from "@/utils/format";
import { reachGoalAiChatSearch } from "@/utils/yandexMetrika";

export interface SearchSelectContext {
  results: SearchIndexEntry[];
  index: number;
  query: string;
}

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  results: SearchIndexEntry[];
  loading: boolean;
  error: string | null;
  totalHint: string | null;
  onSelect: (
    messageId: string,
    hit: SearchIndexEntry,
    ctx: SearchSelectContext
  ) => void;
}

function SearchIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20L16 16" strokeLinecap="round" />
    </svg>
  );
}

export function SearchBar({
  query,
  onQueryChange,
  results,
  loading,
  error,
  totalHint,
  onSelect,
}: Props) {
  const [panelOpen, setPanelOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!panelOpen) return;

    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setPanelOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [panelOpen]);

  useEffect(() => {
    if (panelOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [panelOpen]);

  function pick(item: SearchIndexEntry, sorted: SearchIndexEntry[]) {
    const index = sorted.findIndex((r) => String(r.id) === String(item.id));
    onSelect(item.id, item, {
      results: sorted,
      index: index >= 0 ? index : 0,
      query: query.trim(),
    });
    setPanelOpen(false);
  }

  function togglePanel() {
    setPanelOpen((open) => {
      if (!open) reachGoalAiChatSearch();
      return !open;
    });
  }

  const showResults = panelOpen && query.trim().length >= 2;

  return (
    <div className="search-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`search-toggle ${panelOpen ? "search-toggle--active" : ""}`}
        onClick={togglePanel}
        aria-label="Поиск по архиву"
        aria-expanded={panelOpen}
      >
        <SearchIcon />
      </button>

      {panelOpen && (
        <div className="search-panel" role="dialog" aria-label="Поиск">
          <div className="search-field">
            <SearchIcon />
            <input
              ref={inputRef}
              type="search"
              className="search-input"
              placeholder={
                loading
                  ? "Ищем в архиве…"
                  : "Найти в архиве — маркировка, 1С, ТСД…"
              }
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
            />
            <button
              type="button"
              className="search-close"
              onClick={() => setPanelOpen(false)}
              aria-label="Закрыть поиск"
            >
              ×
            </button>
          </div>
          {error ? (
            <p className="search-hint search-hint--error" role="status">
              {error}
            </p>
          ) : totalHint ? (
            <p className="search-hint" role="status">
              {totalHint} · архив (лента + RAG)
            </p>
          ) : null}
          {showResults && (
            <ul className="search-results" role="listbox">
              {loading && results.length === 0 ? (
                <li className="search-empty">Поиск…</li>
              ) : results.length === 0 ? (
                <li className="search-empty">
                  Ничего не нашли — попробуйте другие слова
                </li>
              ) : (
                results.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="search-result"
                      onClick={() => pick(item, results)}
                    >
                      <span className="search-result-meta">
                        <span className="search-result-date">
                          {formatSearchResultDate(item.date)}
                        </span>
                        <span className="search-result-author">{item.author}</span>
                      </span>
                      <span className="search-result-preview">
                        {item.preview}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
